import {
  AutoAwesome as AutoAwesomeIcon,
  ContentCopy as ContentCopyIcon,
  Inventory as InventoryIcon,
} from "@mui/icons-material";
import clsx from "clsx";
import { FC, MouseEventHandler, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { AccessControl, MuiButton } from "@ryde/controls";
import {
  StationRouteContext,
  useCrudEditContext,
  useCrudNavigate,
  useDynamicFormsFetch,
  useModalConfirm,
  useStationRouteEdit,
} from "@ryde/redux";
import {
  ConfirmResult,
  EditFormMode,
  IDrawersCommonProps,
  IInjectedDrawerProps,
  isInnerItemEditingAtom,
  IStationRouteDto,
  loadersEditFormWithApiDataSF,
  Log,
  useHasPermissions,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { useEditFormInner } from "../../_core";
import { useRideSaveRoutePath } from "../../../drawers";
import { EditFormFooterButton } from "../../../right";
import {
  StationRouteFooterLeftButtonsStyled,
  StationRouteFooterRightButtonsStyled,
  StationRouteFooterStyled,
} from "./station-route.footer.styled";
import { useStationRouteConvertToCustomized } from "./use-station-route-convert-to-customized";

export const StationRouteFooter: FC<
  IDrawersCommonProps & IInjectedDrawerProps
> = ({ onCloseDrawer, resolveFn }) => {
  const { t } = useTranslation();
  const {
    entity,
    mode,
    data: { dto: stationRouteDto, isEdit },
  } = useCrudEditContext<IStationRouteDto>();
  const { rideOpenerId } = useContext(StationRouteContext);

  const { navigateToList } = useCrudNavigate("stationRoute");
  const { fetchTableRows } = useDynamicFormsFetch("stationRoute");
  const { saveStationRoute, cleanupStationRouteAtoms } = useStationRouteEdit();
  const { onConvertToCustomizedClick } = useStationRouteConvertToCustomized();
  const stationRouteId = stationRouteDto?.id;
  const isDataLoading = useRecoilValue(loadersEditFormWithApiDataSF(entity));
  const { confirmDialogResult } = useModalConfirm();

  const { deleteStationRouteHandler } = useRideSaveRoutePath();
  const { onDuplicateClick, closeSidebar, onArchiveClick, closeSidebarOk } =
    useEditFormInner({
      onCloseDrawer,
      resolveFn,
    });
  const { hasPermissions: hasUpdatePermission } = useHasPermissions({
    entity: "stationRoute",
    action: "update",
  });

  const onSaveClick: MouseEventHandler<HTMLButtonElement> = async (
    e,
  ): Promise<void> => {
    e.preventDefault();

    try {
      if (!stationRouteDto) {
        Log.warnAndToast(t(`general.RIDES_ACTIONS_WARNINGS.INVALID_DATA`));
        return;
      }

      const stationRouteResult = await saveStationRoute(stationRouteDto);

      if (stationRouteResult.success) {
        cleanupStationRouteAtoms();

        if (mode !== EditFormMode.CrudRoute) {
          closeSidebarOk(stationRouteResult.data);
        } else {
          navigateToList();
          fetchTableRows();
        }
      } else {
        warnAndToastSimpleResult(stationRouteResult, t);
      }
    } catch (error) {
      Log.warnAndToast(t(`general.RIDES_ACTIONS_WARNINGS.SAVING_ERROR`), error);
    }
  };

  const onDuplicateClickEvent: MouseEventHandler<HTMLButtonElement> = async (
    e,
  ): Promise<void> => {
    e.preventDefault();
    const confirmationResult = await confirmDialogResult({
      title: t("general.DUPLICATE_CONFIRM"),
      message: t(`general.ITEM_DUPLICATE_MESSAGE`),
    });

    if (confirmationResult !== ConfirmResult.OK) {
      return;
    }

    cleanupStationRouteAtoms();
    onDuplicateClick();
  };

  const onArchiveClickEvent: MouseEventHandler<HTMLButtonElement> = async (
    e,
  ): Promise<void> => {
    e.preventDefault();

    if (mode !== EditFormMode.UnboundDrawer) {
      const confirmationResult = await confirmDialogResult({
        title: t("ride.SAVE_ROUTE.SAVED_PATH_CONFIRM_TITLE"),
        message: t("ride.SAVE_ROUTE.SAVED_PATH_DELETE_CONFIRM_TEXT"),
      });

      if (confirmationResult !== ConfirmResult.OK) {
        Log.infoAndToast(
          t("stationRoute.DELETE_STATION_ROUTE_CANCELED_BY_USER"),
        );
        return;
      }

      onArchiveClick();
      return;
    }

    const result = await deleteStationRouteHandler(stationRouteId);

    if (result?.success) {
      closeSidebar();
    }
  };

  const isDuplicateButtonVisible = Boolean(
    stationRouteId && isEdit && !rideOpenerId,
  );
  const isConvertToCustomizedVisible = Boolean(
    stationRouteId && isEdit && !stationRouteDto?.customized,
  );
  const isArchiveButtonVisible = Boolean(stationRouteId && isEdit);
  const isInnerItemEditing = useRecoilValue(isInnerItemEditingAtom);

  return (
    <StationRouteFooterStyled
      className={clsx(
        "StationRouteDrawer-Footer",
        isInnerItemEditing && "inner-item-editing",
      )}
    >
      <StationRouteFooterLeftButtonsStyled>
        <AccessControl entity="stationRoute" action="delete">
          {isArchiveButtonVisible && (
            <EditFormFooterButton
              onClick={onArchiveClickEvent}
              disabled={isDataLoading}
            >
              <InventoryIcon className="footer_svg_icon" />
              <span>{t(`general.ARCHIVE`)}</span>
            </EditFormFooterButton>
          )}
        </AccessControl>
        <AccessControl entity="stationRoute" action="create">
          {isDuplicateButtonVisible && (
            <EditFormFooterButton
              onClick={onDuplicateClickEvent}
              disabled={isDataLoading}
            >
              <ContentCopyIcon className="footer_svg_icon" />
              <span>{t(`general.DUPLICATE`)}</span>
            </EditFormFooterButton>
          )}
        </AccessControl>
        <AccessControl entity="stationRoute" action="update">
          {isConvertToCustomizedVisible && (
            <EditFormFooterButton
              onClick={onConvertToCustomizedClick}
              disabled={isDataLoading}
            >
              <AutoAwesomeIcon className="footer_svg_icon" />
              <span>{t("stationRoute.CONVERT_TO_CUSTOMIZED")}</span>
            </EditFormFooterButton>
          )}
        </AccessControl>
      </StationRouteFooterLeftButtonsStyled>

      <StationRouteFooterRightButtonsStyled>
        <MuiButton
          muiVariant={"outlined"}
          color={"secondary"}
          onClick={() => {
            cleanupStationRouteAtoms();
            closeSidebar();
          }}
          loading={isDataLoading}
        >
          {t(hasUpdatePermission ? "general.CANCEL" : "general.CLOSE")}
        </MuiButton>
        <AccessControl
          entity="stationRoute"
          action={isEdit ? "update" : "create"}
        >
          <MuiButton
            loading={isDataLoading}
            disabled={isDataLoading || isInnerItemEditing}
            onClick={onSaveClick}
            width={140}
            muiStyle={rideOpenerId ? "primary" : "secondary"}
          >
            {t(rideOpenerId ? "general.NEXT" : "general.SAVE")}
          </MuiButton>
        </AccessControl>
      </StationRouteFooterRightButtonsStyled>
    </StationRouteFooterStyled>
  );
};
